import { useProductDetail } from '@/contexts/ProductDetailContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCartStore } from '@/store/cartStore';
import { useFavoritesStore } from '@/store/favoritesStore';
import { useUserStore } from '@/store/userStore';
import { createProductSlug } from '@/utils/slug';
import { AlertCircle, Heart, Minus, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneNumberModal } from './PhoneNumberModal';
import { toast } from './ToastProvider';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Drawer, DrawerContent } from './ui/drawer';
import { Sheet, SheetContent } from './ui/sheet';

export function ProductDetailSheet() {
  const isMobile = useIsMobile();
  const { isOpen, currentProduct, closeProductDetail } = useProductDetail();
  const { addItem, getItem, updateQuantity } = useCartStore();
  const { isFavorite, addFavorite, removeFavorite } = useFavoritesStore();
  const navigate = useNavigate();
  const phoneNumber = useUserStore(s => s.phoneNumber);
  const setPhoneNumber = useUserStore(s => s.setPhoneNumber);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [pendingFavorite, setPendingFavorite] = useState(false);

  if (!currentProduct) return null;

  const favorite = currentProduct.id ? isFavorite(currentProduct.id) : false;
  const availableQuantity = currentProduct.quantity ?? 0;
  const cartItem = getItem(currentProduct.id);
  const quantityInCart = cartItem?.quantity ?? 0;
  const isPOM = currentProduct.category?.toUpperCase() === 'POM';

  const handleAddToCart = () => {
    addItem({
      id: currentProduct.id,
      brand_name: currentProduct.brand_name,
      price: currentProduct.price,
      image: currentProduct.image_url || null,
      category: currentProduct.category || null,
      availableQuantity,
    });

    toast.success('Product added to cart');
  };

  const handleIncrease = () => {
    if (quantityInCart < availableQuantity) {
      updateQuantity(currentProduct.id, quantityInCart + 1);
    }
  };

  const handleDecrease = () => {
    updateQuantity(currentProduct.id, quantityInCart - 1);
  };

  const toggleFavorite = (providedPhone?: string) => {
    if (!currentProduct) return;

    const phoneToUse = providedPhone || phoneNumber;

    if (!phoneToUse) {
      setPendingFavorite(true);
      setShowPhoneModal(true);
      return;
    }

    if (favorite) {
      removeFavorite(currentProduct.id);
      toast.success(
        `${currentProduct.brand_name} has been removed from your favorites`
      );
    } else {
      addFavorite(currentProduct);
      toast.success(`${currentProduct.brand_name} has been added to your favorites`);
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite();
  };

  const handlePhoneSubmit = (phone: string) => {
    setPhoneNumber(phone);
    setShowPhoneModal(false);
    if (pendingFavorite) {
      setPendingFavorite(false);
      toggleFavorite(phone);
    }
  };

  const handleViewFullDetails = () => {
    const slug = createProductSlug(currentProduct.id, currentProduct.brand_name);
    navigate(`/product/${slug}`);
    closeProductDetail();
  };

  const renderProductImage = () => (
    <div className="relative h-48 md:h-56 mb-4 rounded-md overflow-hidden bg-[var(--dispensary-gray-100)]">
      <img
        src={currentProduct.image_url || '/assets/medication-placeholder.webp'}
        alt={currentProduct.brand_name}
        className="w-full h-full object-cover"
        onError={e => {
          e.currentTarget.src = '/assets/medication-placeholder.webp';
        }}
      />
    </div>
  );

  const renderPrescriptionNotice = () => {
    if (!isPOM) return null;

    return (
      <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md mb-3">
        <AlertCircle className="h-3.5 w-3.5 text-amber-800 flex-shrink-0" />
        <p className="text-xs text-amber-800">
          This medication requires a valid prescription.
        </p>
      </div>
    );
  };

  const renderActionButtons = () => (
    <div className="flex items-center gap-3 py-1.5">
      {availableQuantity === 0 ? (
        <Button className="flex-1 rounded-full h-9 text-xs" disabled>
          Out of Stock
        </Button>
      ) : quantityInCart > 0 ? (
        <div className="flex-1 flex items-center justify-between rounded-full bg-primary h-9 px-2">
          <button
            type="button"
            onClick={handleDecrease}
            className="flex items-center justify-center rounded-full text-primary-foreground w-6 h-6"
          >
            <Minus size={12} />
          </button>
          <span className="text-sm font-medium text-primary-foreground">
            {quantityInCart}
          </span>
          <button
            type="button"
            onClick={handleIncrease}
            disabled={quantityInCart >= availableQuantity}
            className="flex items-center justify-center rounded-full text-primary-foreground disabled:opacity-50 w-6 h-6"
          >
            <Plus size={12} />
          </button>
        </div>
      ) : (
        <Button
          className="flex-1 rounded-full h-9 text-xs"
          onClick={handleAddToCart}
        >
          Add to Cart
        </Button>
      )}
      <Button
        variant="outline"
        onClick={handleViewFullDetails}
        className="flex-1 rounded-full h-9 text-xs"
      >
        View Full Details
      </Button>
    </div>
  );

  const content = (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between items-start">
          <div className="mb-4">
            {currentProduct.category && (
              <Badge
                variant="outline"
                className="mb-1. 5 text-[10px] px-1.5 py-0"
              >
                {currentProduct.category}
              </Badge>
            )}
            <div className="flex items-center justify-between mt-1.5">
              <h2 className="text-lg md:text-xl font-semibold">
                {currentProduct.brand_name}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                className={`ml-3 p-0 h-7 w-7 bg-transparent shadow-none border-none hover:bg-transparent focus:bg-transparent active:bg-transparent ${
                  favorite ? 'text-[var(--famasi-red-500)]' : ''
                }`}
                onClick={handleToggleFavorite}
                aria-pressed={favorite}
              >
                <Heart
                  className="h-4 w-4"
                  fill={favorite ? 'var(--famasi-red-500)' : 'none'}
                  stroke={favorite ? 'var(--famasi-red-500)' : 'currentColor'}
                />
              </Button>
            </div>
            <p className="text-lg md:text-xl font-semibold">
              ₦{currentProduct.price.toLocaleString()}
            </p>
          </div>
        </div>
        {renderPrescriptionNotice()}
        {renderProductImage()}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="prose max-w-none">
          <h5 className="mb-1.5 text-sm font-medium text-[var(--dispensary-gray-800)]">
            Product Details
          </h5>
          <ul className="list-disc pl-4 mt-1.5 space-y-0.5 text-xs text-[var(--dispensary-gray-700)]">
            <li>Brand Name: {currentProduct.brand_name}</li>
            <li>Category: {currentProduct.category || 'N/A'}</li>
            <li>Strength: {currentProduct.strength || 'N/A'}</li>
            <li>
              Active Ingredient: {currentProduct.active_ingredient || 'N/A'}
            </li>
            <li>Type: {currentProduct.type || 'N/A'}</li>
            <li>Dosage Form: {currentProduct.dosage_form || 'N/A'}</li>
            <li>Pack Size: {currentProduct.pack_size || 'N/A'}</li>
            <li>Class: {currentProduct.class || 'N/A'}</li>
            <li>Quantity Available: {currentProduct.quantity || 'N/A'}</li>
            <li>SKU: {currentProduct.id}</li>
          </ul>
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <>
        <Drawer open={isOpen} onOpenChange={closeProductDetail}>
          <DrawerContent className="max-h-[85vh] bg-white p-0 flex flex-col h-[85vh]">
            <div className="flex flex-col h-full w-full max-w-md mx-auto">
              <div className="px-4 pt-4 flex-1 min-h-0 overflow-y-auto">
                {content}
              </div>
              <div className="border-t bg-white px-4 py-3">
                {renderActionButtons()}
              </div>
            </div>
          </DrawerContent>
        </Drawer>

        <PhoneNumberModal
          open={showPhoneModal}
          onClose={() => setShowPhoneModal(false)}
          onSubmit={handlePhoneSubmit}
        />
      </>
    );
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={closeProductDetail}>
        <SheetContent className="w-full sm:max-w-lg bg-white flex flex-col overflow-hidden p-0 h-screen max-h-screen">
          <div className="flex flex-col h-full w-full">
            <div className="px-4 pt-4 flex-1 min-h-0 overflow-y-auto">
              {content}
            </div>
            <div className="border-t bg-white px-4 py-3">
              {renderActionButtons()}
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <PhoneNumberModal
        open={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onSubmit={handlePhoneSubmit}
      />
    </>
  );
}
